import axios from 'axios';
import _ from 'lodash';

class Api {
  async init(config) {
    this.updateConfig(config);
    this.httpClient = axios.create({
      baseURL: this.serverUrl,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    this.initHttpClientInterceptors();
  }

  initHttpClientInterceptors() {
    this.httpClient.interceptors.response.use(null, async (error) => {
      if (
        _.get(error, 'config.headers.Authorization')
        && [401].includes(_.get(error, 'response.status'))
        && !['WarpwireAuthorizationError'].includes(_.get(error, 'response.data.name'))
      ) {
        this.refreshCounter += 1;
        if (this.refreshCounter >= 4) return Promise.reject('Unable to refresh the PlayPosit token associated with your account.');
        try {
          await this.refreshToken();
          return axios.request(error.config);
        } catch(e) {
          return Promise.reject(e);
        }
      }
      return Promise.reject({
        ..._.get(error, 'response.data', {}),
        message: (() => {
          if (_.get(error, 'response.data.message')) return error.response.data.message;
          if (_.get(error, 'response.data.msg')) return error.response.data.msg;
          if (_.get(error, 'response.data.statusText')) return error.response.data.statusText;
          if (_.get(error, 'message')) return error.message;
          if (_.get(error, 'msg')) return error.msg;
          if (_.get(error, 'statusText')) return error.statusText;
          return 'An unknown error has occurred.';
        })(),
        status: _.get(error, 'response.status', _.get(error, 'status')),
      });
    });
  }

  updateConfig(config) {
    for (const key in config) {
      if ([
        'anonymous',
        'serverUrl',
        'token',
        'interactionId',
        'bulbId',
        'sectionId',
        'studentId',
      ].includes(key)) this[key] = config[key];
    }
  }

  refreshToken() {
    return this.httpClient.post('authnew/refreshtoken', {
      old_token: this.token,
    });
  }

  getServerUrl() {
    return this.serverUrl;
  }

  getToken() {
    return this.token;
  }

  getSectionId() {
    return this.sectionId;
  }

  uploadAudio(formData) {
    return this.httpClient.post('upload/audio', formData);
  }

  uploadImage(formData) {
    return this.httpClient.post('upload/image', formData);
  }

  getStudentsInSection(sectionId, anonymous) {
    return this.httpClient.get(`api/1.1/sections/${sectionId}/students${anonymous ? '?anonymous=true' : ''}`);
  }

  getComponentData({
    anonymous,
    requiredModels,
    mode,
  } = {}) {
    return this.httpClient.post('interaction/component', {
      anonymous,
      interactionId: this.interactionId,
      sectionId: this.sectionId,
      bulbId: this.bulbId,
      studentId: this.studentId,
      requiredModels,
      mode,
    });
  }

  getDiscussionComments({
    anonymous,
    studentId,
    pageSize,
    searchTerm,
    includeArchived = true,
    timestampBefore,
    timestampAfter,
    parentId = false,
    idGreaterThan,
    displayTimeGreaterThan,
    displayTimeLessThan,
    order,
  } = {}) {
    return this.httpClient.post(`discussionV2/${this.interactionId}/attempts`, {
      anonymous,
      sectionId: this.sectionId,
      bulbId: this.bulbId,
      studentId,
      pageSize,
      searchTerm,
      includeArchived,
      timestampBefore,
      timestampAfter,
      parentId,
      idGreaterThan,
      displayTimeGreaterThan,
      displayTimeLessThan,
      order,
    });
  }

  downloadDiscussionAttempts({
    anonymous,
  } = {}) {
    return this.httpClient.get(`discussionV2/${this.interactionId}/download/${this.sectionId}${anonymous ? '?anonymous=true' : ''}`);
  }

  createDiscussionComment(discussionAttempt) {
    return this.httpClient.post('discussionV2', discussionAttempt);
  }

  updateDiscussionComment(discussionAttempt) {
    return this.httpClient.put(`discussionV2/${discussionAttempt.id}`, discussionAttempt);
  }

  deleteDiscussionComment(discussionAttemptId) {
    return this.httpClient.delete(`discussionV2/${discussionAttemptId}`);
  }

  updateStudentCommentViews(feedbackData) {
    return this.httpClient.put('learner/feedback/views', feedbackData);
  }

  updateTeacherCommentViews(feedbackData) {
    return this.httpClient.put('instructor/feedback/views', feedbackData);
  }
}
const api = new Api();
export default api;